/**
 * 动态适配组件类型的入口集中处理，可以根据类型分发处理至 dynamic-types.tsx 组件中处理
 */

import { Switch } from '@tencent/udesign/dist';
import classNames from 'classnames';
import React from 'react';
import { SELECT_YES_NO_DATA } from '../../libs/enum/select-extension-value';
import {
  DynamicRenderStyleMode,
  FieldType,
  IConfigField,
  IValue,
} from '../../model';
import {
  DynamicCheckbox,
  DynamicDate,
  DynamicSelect,
  DynamicSelectUser,
  DynamicTextEntry,
  DynamicAdvancedSelect,
  DynamicMultiDate,
} from './components';
import { DynamicCascaderMulti } from './components/cascader-multi';
import { DynamicRadio } from './components/radio';
import { DynamicTreeSelect } from './components/tree-select';

interface IProps<T> {
  readonly fieldConfig: IConfigField<T>;
  readonly onChanged: <T>(key: string, value: T) => void;
  readonly selectedValue: IValue;
  readonly mode: DynamicRenderStyleMode;
  readonly className?: string;
}

// 适配所有的类型和值类型
export const DynamicComponentRender = <T,>(props: IProps<T>) => {
  const { fieldConfig, mode } = props;
  const fieldType = fieldConfig.filter_type || FieldType.text;
  const selectedValue = props.selectedValue || {};

  const isTableBodyModeView = [DynamicRenderStyleMode.tableBody].includes(mode);
  const isFilterModeView = [DynamicRenderStyleMode.filter].includes(mode);

  // 特殊处理：
  // 开关组件，在 filter 中以 select 展示，在 Table 中则是以 switch展示
  if (fieldType === FieldType.switch) {
    if (isTableBodyModeView) {
      const checked = selectedValue.value === 'true';
      const onChangedSwitch = (checked: boolean) => {
        if (props.onChanged) {
          props.onChanged<string>(fieldConfig.key, `${checked}`);
        }
      };
      return (
        <Switch
          type="success"
          className={classNames('pointer-all', props.className)}
          innerLabel="否"
          innerLabelChecked="是"
          checked={checked}
          onChange={onChangedSwitch}
        />
      );
    }

    const onChangedSwitch = (key: string, value: any) => {
      if (props.onChanged) {
        props.onChanged<string>(key, value);
      }
    };
    return (
      <DynamicSelect
        fieldConfig={fieldConfig}
        onChanged={onChangedSwitch}
        selectedValue={selectedValue}
        options={SELECT_YES_NO_DATA}
        isFilterModeView={isFilterModeView}
        className={props.className}
      />
    );
  }

  // 下拉框
  if (
    FieldType.select === fieldType ||
    FieldType.select_multiple === fieldType
  ) {
    return (
      <DynamicSelect
        fieldConfig={fieldConfig}
        onChanged={props.onChanged}
        selectedValue={selectedValue}
        isFilterModeView={isFilterModeView}
        className={props.className}
      />
    );
  }
  // 下拉框 选中项置顶
  if (
    FieldType.advanced_select === fieldType ||
    FieldType.advanced_select_multiple === fieldType
  ) {
    return (
      <DynamicAdvancedSelect
        fieldConfig={fieldConfig}
        onChanged={props.onChanged}
        selectedValue={selectedValue}
        isFilterModeView={isFilterModeView}
        className={props.className}
      />
    );
  }
  // 树型选择下拉框
  if (FieldType.tree_select === fieldType) {
    return (
      <DynamicTreeSelect
        fieldConfig={fieldConfig}
        onChanged={props.onChanged}
        selectedValue={selectedValue}
        isFilterModeView={isFilterModeView}
        className={props.className}
      />
    );
  }

  // 多选框
  if (FieldType.checkbox === fieldType) {
    return (
      <DynamicCheckbox
        fieldConfig={fieldConfig}
        onChanged={props.onChanged}
        selectedValue={selectedValue}
        isFilterModeView={isFilterModeView}
        className={props.className}
      />
    );
  }

  // 单选框
  if (FieldType.radio === fieldType) {
    return (
      <DynamicRadio
        fieldConfig={fieldConfig}
        onChanged={props.onChanged}
        selectedValue={selectedValue}
        isFilterModeView={isFilterModeView}
        className={props.className}
      />
    );
  }

  // 人名
  if (
    fieldType &&
    [FieldType.user_chooser, FieldType.user_chooser_multiple].includes(
      fieldType
    )
  ) {
    return (
      <DynamicSelectUser
        fieldConfig={fieldConfig}
        onChanged={props.onChanged}
        selectedValue={selectedValue}
        isFilterModeView={isFilterModeView}
        className={props.className}
      />
    );
  }

  // 时间
  if (FieldType.date === fieldType || FieldType.date_range === fieldType) {
    return (
      <DynamicDate
        fieldConfig={fieldConfig}
        onChanged={props.onChanged}
        selectedValue={selectedValue}
        isFilterModeView={isFilterModeView}
        className={props.className}
      />
    );
  }

  if (FieldType.date_multiple === fieldType) {
    return (
      <DynamicMultiDate
        fieldConfig={fieldConfig}
        onChanged={props.onChanged}
        selectedValue={selectedValue}
        isFilterModeView={isFilterModeView}
        className={props.className}
      />
    );
  }

  if (FieldType.cascader_multiple === fieldType) {
    return (
      <DynamicCascaderMulti
        fieldConfig={fieldConfig}
        onChanged={props.onChanged}
        selectedValue={selectedValue}
        isFilterModeView={isFilterModeView}
        className={props.className}
      />
    );
  }

  // 普通文本 input
  return (
    <DynamicTextEntry
      fieldConfig={fieldConfig}
      onChanged={props.onChanged}
      selectedValue={selectedValue}
      isFilterModeView={isFilterModeView}
      className={props.className}
    />
  );
};
